import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { AccessLevel } from "../portal-auth";
import { SUPPORT_EMAIL } from "../lib/portal-auth-errors";
import type { PortalAuthErrorCode } from "../lib/portal-auth-errors";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { getPortalUrl } from "../lib/portal-url";
import { getGuestPromptDelayMs } from "../lib/dev-access-override";
import { isMobileViewport } from "../lib/is-mobile-viewport";
import { DevAccessPill } from "./DevAccessPill";
import { GuestAccessPrompt } from "./GuestAccessPrompt";
import { MobileWarningModal } from "./MobileWarningModal";
import logoMark from "../assets/brand/logo-mark.png";
import "./layout.css";

export interface ToolLayoutProps {
  toolName: string;
  toolSubtitle?: string;
  access: AccessLevel | null;
  userName: string | null;
  loading?: boolean;
  error?: string | null;
  errorCode?: PortalAuthErrorCode | null;
  portalUrl?: string;
  isGuest?: boolean;
  toolId?: string | null;
  toolSlug?: string;
  onOpenSettings?: () => void;
  onShowOnboarding?: () => void;
  /** Extra knoppen in de header, direct na de ?-knop. */
  headerExtraActions?: ReactNode;
  /** Sla de mobiele waarschuwing over (bijv. bewuste mobiele flows). */
  skipMobileWarning?: boolean;
  /** Geen “Onbeperkt gratis verder?”-prompt (auto én via header). */
  skipGuestPrompt?: boolean;
  /** Opslaan / openen-knoppen in de header (bijv. via ToolFileActions). */
  fileActions?: ReactNode;
  children: ReactNode;
}

function accessBadgeClass(access: AccessLevel | null, isGuest: boolean): string {
  if (isGuest) return "jari-badge jari-badge--probeer jari-badge--button";
  if (access === "PAID") return "jari-badge jari-badge--paid";
  if (access === "FREE") return "jari-badge jari-badge--free";
  return "jari-badge jari-badge--none";
}

function accessBadgeLabel(access: AccessLevel | null, isGuest: boolean): string {
  if (isGuest) return "PROBEER";
  return access ?? "NONE";
}

export function ToolLayout({
  toolName,
  toolSubtitle,
  access,
  userName,
  loading,
  error,
  errorCode,
  portalUrl,
  isGuest = false,
  toolId,
  toolSlug,
  onOpenSettings,
  onShowOnboarding,
  headerExtraActions,
  skipMobileWarning = false,
  skipGuestPrompt = false,
  fileActions,
  children,
}: ToolLayoutProps) {
  const portal = getPortalUrl(portalUrl);
  const [guestPromptOpen, setGuestPromptOpen] = useState(false);
  const [mobileWarningOpen, setMobileWarningOpen] = useState(false);
  useDocumentTitle(toolName);

  const guestRedirectTo = useMemo(() => {
    if (toolId) return `/api/tools/open?toolId=${encodeURIComponent(toolId)}`;
    if (toolSlug) return `/tools?highlight=${encodeURIComponent(toolSlug)}`;
    return "/tools";
  }, [toolId, toolSlug]);

  const buyUrl = useMemo(
    () => (toolSlug ? `${portal}/kopen/${encodeURIComponent(toolSlug)}` : null),
    [portal, toolSlug],
  );

  useEffect(() => {
    if (!isGuest || skipGuestPrompt) return;
    const id = window.setTimeout(() => setGuestPromptOpen(true), getGuestPromptDelayMs());
    return () => window.clearTimeout(id);
  }, [isGuest, skipGuestPrompt]);

  useEffect(() => {
    if (loading || skipMobileWarning || !isMobileViewport()) return;
    setMobileWarningOpen(true);
  }, [loading, skipMobileWarning]);

  return (
    <div className="jari-tool-layout">
      <header className="jari-tool-header">
        <div className="jari-tool-header__brand">
          <a
            href={portal}
            className="jari-tool-header__logo-link"
            target="_blank"
            rel="noopener noreferrer"
            title="Interact GGZ portal"
          >
            <img src={logoMark} alt="" className="jari-tool-header__logo" aria-hidden="true" />
          </a>
          <div>
            <h1 className="jari-tool-header__title">{toolName}</h1>
            {toolSubtitle && (
              <p className="jari-tool-header__subtitle">{toolSubtitle}</p>
            )}
            {userName && !loading && (
              <p className="jari-tool-header__user">{userName}</p>
            )}
          </div>
          {!loading && access && (
            isGuest && !skipGuestPrompt ? (
              <button
                type="button"
                className={accessBadgeClass(access, isGuest)}
                onClick={() => setGuestPromptOpen(true)}
                title="Registreer voor onbeperkte gratis toegang"
              >
                {accessBadgeLabel(access, isGuest)}
              </button>
            ) : (
              <span className={accessBadgeClass(access, isGuest)}>
                {accessBadgeLabel(access, isGuest)}
              </span>
            )
          )}
        </div>
        <div className="jari-tool-header__end">
          {fileActions && (
            <div className="jari-tool-header__file">{fileActions}</div>
          )}
          <div className="jari-tool-header__actions">
            {onShowOnboarding && (
              <button
                type="button"
                className="jari-btn jari-btn-icon"
                onClick={onShowOnboarding}
                title="Uitleg tonen"
                aria-label="Uitleg tonen"
              >
                ?
              </button>
            )}
            {headerExtraActions}
            {onOpenSettings && (
              <button
                type="button"
                className="jari-btn jari-btn-icon"
                onClick={onOpenSettings}
                title="Instellingen"
                aria-label="Instellingen"
              >
                ⚙
              </button>
            )}
            {!loading && isGuest && !skipGuestPrompt && (
              <button
                type="button"
                className="jari-btn jari-btn--primary"
                onClick={() => setGuestPromptOpen(true)}
              >
                Inloggen/registreren
              </button>
            )}
            {!loading && isGuest && skipGuestPrompt && (
              <a href={portal} className="jari-btn jari-btn--primary">
                Inloggen/registreren
              </a>
            )}
            {!loading && !isGuest && access === "FREE" && buyUrl && (
              <a href={buyUrl} className="jari-btn jari-btn--primary">
                Koop
              </a>
            )}
            <a
              href={portal}
              className="jari-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              Portal
            </a>
          </div>
        </div>
      </header>

      <main className="jari-tool-main">
        {loading && (
          <div className="jari-tool-auth-banner jari-tool-auth-banner--loading">
            Licentie controleren…
          </div>
        )}
        {error && !loading && (
          <div className="jari-tool-auth-banner jari-tool-auth-banner--error">
            {errorCode === "session_recover_failed" ? (
              <>
                De sessie kon niet worden hersteld. Waarschijnlijk blokkeert je browser
                cookies of strikte privacy-instellingen het plaatsen ervan. Excuses voor het
                ongemak. Blijft het probleem? Mail naar{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
              </>
            ) : (
              <>
                {error}{" "}
                <a href={portal}>Naar het portal</a>
              </>
            )}
          </div>
        )}
        {children}
      </main>

      <footer className="jari-tool-footer">
        <span>Privacy first: jouw gegevens blijven op je eigen apparaat.</span>
        <a
          href="https://interactggz.nl/privacy/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacybeleid
        </a>
      </footer>

      {isGuest && !skipGuestPrompt && (
        <GuestAccessPrompt
          open={guestPromptOpen}
          onClose={() => setGuestPromptOpen(false)}
          portalUrl={portalUrl}
          redirectTo={guestRedirectTo}
        />
      )}

      <MobileWarningModal
        open={mobileWarningOpen}
        onClose={() => setMobileWarningOpen(false)}
      />

      {import.meta.env.DEV && <DevAccessPill />}
    </div>
  );
}
